import {
  parseErrorResponse,
  getFieldErrors,
  getPermissionDetails
} from './errorHandler.js';
import {
  isValidationError,
  isPermissionError,
  isServerError
} from './errorTypes.js';
import { useToast } from 'vue-toastification';

/**
 * Error display mode configuration
 * Options: 'toast' or 'alert'
 * - 'toast': Use vue-toastification toast notifications (default)
 * - 'alert': Use this.$alert for error display
 */
export const ERROR_DISPLAY_MODE = 'alert'; // Change to 'alert' to use $alert instead

/**
 * Format error message structure
 * @param {string} title - Error title/message
 * @param {string[]} details - Array of detail strings to display
 * @param {string} footer - Footer text (e.g., reference ID)
 * @returns {string} Formatted plain text string
 */
function formatErrorHtml(title, details, footer) {
  const separator = ERROR_DISPLAY_MODE == 'toast' ? '\n' : '<br>'; 
  let message = title;
  if (details && details.length > 0) {
    message += separator + details.join(separator);
  }

  if (footer) {
    message += separator + footer;
  }

  return message;
}

/**
 * Format permission error with privilege info
 * @param {import('./errorTypes.js').ErrorResponse} errorResponse - Parsed error response
 * @param {Object} i18n - i18n instance with t() method
 * @returns {{title: string, details: string[], footer: string}} Formatted error parts
 */
function formatPermissionError(errorResponse, i18n) {
  // Get translated message or fallback to backend message
  let title = errorResponse.message;
  if (errorResponse.code && i18n && typeof i18n.t === 'function') {
    const translation = i18n.t(errorResponse.code);
    if (translation !== errorResponse.code) {
      title = translation;
    }
  }

  const details = [];
  const permissionDetails = getPermissionDetails(errorResponse);

  if (permissionDetails && permissionDetails.requiredPrivilege) {
    // Get translated "Required privilege:" prefix
    const privilegeLabel = i18n && typeof i18n.t === 'function'
      ? i18n.t('ERROR_REQUIRED_PRIVILEGE')
      : 'Required privilege:';
    details.push(`${privilegeLabel} ${permissionDetails.requiredPrivilege}`);
  }

  // Get translated "Contact your administrator for access" message
  const adminContactMsg = i18n && typeof i18n.t === 'function'
    ? i18n.t('ERROR_CONTACT_ADMIN')
    : 'Contact your administrator for access.';
  details.push(adminContactMsg);

  return { title, details, footer: null };
}

/**
 * Format server error with reference ID
 * @param {import('./errorTypes.js').ErrorResponse} errorResponse - Parsed error response
 * @param {Object} i18n - i18n instance with t() method
 * @returns {{title: string, details: string[], footer: string}} Formatted error parts
 */
function formatServerError(errorResponse, i18n) {
  // Get translated message or fallback to backend message
  let title = errorResponse.message;
  if (errorResponse.code && i18n && typeof i18n.t === 'function') {
    const translation = i18n.t(errorResponse.code);
    if (translation !== errorResponse.code) {
      title = translation;
    }
  }

  let footer = null;
  if (errorResponse.errorId) {
    // Get translated "Reference:" prefix
    const referenceLabel = i18n && typeof i18n.t === 'function'
      ? i18n.t('ERROR_REFERENCE_ID')
      : 'Reference:';
    footer = `${referenceLabel} ${errorResponse.errorId}`;
  }

  return { title, details: [], footer };
}

/**
 * Format validation error with field-specific errors
 * @param {import('./errorTypes.js').ErrorResponse} errorResponse - Parsed error response
 * @param {Object} i18n - i18n instance with t() method
 * @returns {{title: string, details: string[], footer: string}} Formatted error parts
 */
function formatValidationError(errorResponse, i18n) {
  // Get general validation message
  let title = errorResponse.message;
  if (errorResponse.code && i18n && typeof i18n.t === 'function') {
    const translation = i18n.t(errorResponse.code);
    if (translation !== errorResponse.code) {
      title = translation;
    }
  }

  const details = [];
  const fieldErrors = getFieldErrors(errorResponse);

  // Add field-specific errors
  fieldErrors.forEach(fieldError => {
    // Try to translate the field error message using its code
    let fieldMessage = fieldError.message;
    if (fieldError.code && i18n && typeof i18n.t === 'function') {
      const translation = i18n.t(fieldError.code);
      if (translation !== fieldError.code) {
        fieldMessage = translation;
      }
    }
    details.push(`${fieldError.field}: ${fieldMessage}`);
  });

  return { title, details, footer: null };
}

/**
 * Display error notification using toast or alert
 * @param {Object} error - Axios error object
 * @param {Object} i18n - i18n instance with t() method for translations
 * @param {Object} vueInstance - Optional Vue component instance (required when using 'alert' mode)
 */
export function showError(error, i18n, vueInstance = null) {
  const errorResponse = parseErrorResponse(error);

  let title, details, footer;

  // Format based on error type
  if (errorResponse.code && isPermissionError(errorResponse.code)) {
    ({ title, details, footer } = formatPermissionError(errorResponse, i18n));
  } else if (errorResponse.code && isServerError(errorResponse.code)) {
    ({ title, details, footer } = formatServerError(errorResponse, i18n));
  } else if (errorResponse.code && isValidationError(errorResponse.code)) {
    ({ title, details, footer } = formatValidationError(errorResponse, i18n));
  } else {
    // Generic error - use translated message if available
    title = errorResponse.message;
    if (errorResponse.code && i18n && typeof i18n.t === 'function') {
      const translation = i18n.t(errorResponse.code);
      if (translation !== errorResponse.code) {
        title = translation;
      }
    }
    details = [];
    footer = null;
  }

  // Create content
  const content = formatErrorHtml(title, details, footer);

  // Display based on configured mode
  if (ERROR_DISPLAY_MODE === 'alert') {
    if (vueInstance && vueInstance.$alert) {
      // Use $alert with signature: $alert(title, message, type)
      vueInstance.$alert(content, "حدث خطأ", 'error');
    } else {
      console.error('Vue instance with $alert method is required when using alert mode');
      console.error(content);
    }
  } else {
    // Default to toast
    const toast = useToast();
    toast.error(content, {
      timeout: 3000,            // Auto-dismiss after 3 seconds
      closeOnClick: true,       // Allow click to dismiss
      hideProgressBar: false,   // Show progress bar for timeout
      closeButton: 'button',    // Show close button
      rtl: document.dir === 'rtl' // Support RTL languages
    });
  }
}

/**
 * Display multiple errors in a single notification
 * For bulk operations that may have multiple failures
 * @param {Object[]} errors - Array of axios error objects
 * @param {Object} i18n - i18n instance with t() method for translations
 * @param {Object} vueInstance - Optional Vue component instance (required when using 'alert' mode)
 */
export function showBulkErrors(errors, i18n, vueInstance = null) {
  if (!errors || errors.length === 0) {
    return;
  }

  // If only one error, use regular showError
  if (errors.length === 1) {
    showError(errors[0], i18n, vueInstance);
    return;
  }

  // Parse all errors
  const parsedErrors = errors.map(error => parseErrorResponse(error));

  // Create title
  const title = i18n && typeof i18n.t === 'function'
    ? i18n.t('ERROR_MULTIPLE_ERRORS') || `Multiple errors occurred (${errors.length})`
    : `Multiple errors occurred (${errors.length})`;

  // Create numbered list of all error messages
  const details = parsedErrors.map((errorResponse, index) => {
    let message = errorResponse.message;
    if (errorResponse.code && i18n && typeof i18n.t === 'function') {
      const translation = i18n.t(errorResponse.code);
      if (translation !== errorResponse.code) {
        message = translation;
      }
    }
    return `${index + 1}. ${message}`;
  });

  const content = formatErrorHtml(title, details, null);

  // Display based on configured mode
  if (ERROR_DISPLAY_MODE === 'alert') {
    if (vueInstance && vueInstance.$alert) {
      // Use $alert with signature: $alert(title, message, type)
      vueInstance.$alert('', content, 'error');
    } else {
      console.error('Vue instance with $alert method is required when using alert mode');
      console.error(content);
    }
  } else {
    // Default to toast
    const toast = useToast();
    toast.error(content, {
      timeout: 3000,
      closeOnClick: true,
      hideProgressBar: false,
      closeButton: 'button',
      rtl: document.dir === 'rtl'
    });
  }
}

/**
 * Extract validation errors for form display
 * Useful for showing inline field errors in forms
 * @param {Object} error - Axios error object
 * @param {Object} i18n - i18n instance with t() method for translations
 * @returns {{fieldErrors: Map<string, string[]>, generalMessage: string}} Field errors map and general message
 */
export function showValidationErrors(error, i18n) {
  const errorResponse = parseErrorResponse(error);

  // Get general message
  let generalMessage = errorResponse.message;
  if (errorResponse.code && i18n && typeof i18n.t === 'function') {
    const translation = i18n.t(errorResponse.code);
    if (translation !== errorResponse.code) {
      generalMessage = translation;
    }
  }

  // Extract field-level errors
  const fieldErrorsMap = new Map();
  const fieldErrors = getFieldErrors(errorResponse);

  fieldErrors.forEach(fieldError => {
    // Translate field error message if possible
    let message = fieldError.message;
    if (fieldError.code && i18n && typeof i18n.t === 'function') {
      const translation = i18n.t(fieldError.code);
      if (translation !== fieldError.code) {
        message = translation;
      }
    }

    // Add to map (supporting multiple errors per field)
    if (!fieldErrorsMap.has(fieldError.field)) {
      fieldErrorsMap.set(fieldError.field, []);
    }
    fieldErrorsMap.get(fieldError.field).push(message);
  });

  return {
    fieldErrors: fieldErrorsMap,
    generalMessage: generalMessage
  };
}
